<template>
    <AuthLayout>
        <fullscreen-loading v-if="isLoading || isLoadingPdfExport" />
        <CeremonyDownloadLoading :ceremony-id="downloadCeremonyId" v-if="isDownloadingCeremony && downloadCeremonyId" />

        <div class="row mt-3">
            <div class="col-12">
                <div class="card">
                    <div class="card-header pb-3">
                        <div class="d-flex justify-content-between align-items-center">
                            <div class="d-sm-flex w-100 align-items-center">
                                <span class="h4">{{ $t('ceremony.title') }}</span>

                                <button class="btn btn-primary btn-sm ml-auto d-flex align-items-center mt-2 mt-sm-0 w-100 w-sm-auto justify-content-center" @click="currentAction = { action: Action.Create }">
                                    {{ $t('ceremony.create') }}
                                    <icon name="plus" class="ml-1" />
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="card-body">
                        <div class="alert alert-danger" v-if="errorMessage">{{ errorMessage }}</div>
                        <table class="table table-sm table-no-side-paddings table-striped">
                            <thead>
                                <tr>
                                    <ordering @change="loadCeremonies(ceremonies.current_page)" v-model="order" field="name" tag="th">{{ $t('ceremony.deceased_name') }}</ordering>
                                    <ordering @change="loadCeremonies(ceremonies.current_page)" v-model="order" field="code" tag="th">{{ $t('ceremony.code') }}</ordering>
                                    <ordering @change="loadCeremonies(ceremonies.current_page)" v-model="order" field="date" tag="th">{{ $t('ceremony.date') }}</ordering>
                                    <ordering @change="loadCeremonies(ceremonies.current_page)" v-model="order" field="duration" tag="th">{{ $t('ceremony.duration_in_minutes') }}</ordering>
                                    <th>{{ $t('ceremony.user') }}</th>
                                    <th />
                                </tr>
                            </thead>

                            <tbody>
                                <tr>
                                    <td>
                                        <input :placeholder="$t('ceremony.deceased_name')" @change="loadCeremonies(1)" class="form-control form-control-filtering" type="text" v-model="filtering.name" />
                                    </td>
                                    <td>
                                        <input :placeholder="$t('ceremony.code')" @change="loadCeremonies(1)" class="form-control form-control-filtering" type="text" v-model="filtering.code" />
                                    </td>
                                    <td>
                                        <datetime-picker
                                            v-model="filtering.date"
                                            :placeholder="$t('ceremony.date')"
                                            type="date"
                                            style="width: 50%; float: left"
                                            inputClass="form-control form-control-filtering"
                                            class="theme-company"
                                            :format="'dd-MM-yyyy'"
                                            @change="dateChanged"
                                        />

                                        <select style="width: 50%; padding: 2px 8px;" @change="datePresetChanged" class="form-control form-control-filtering" v-model="datePreset">
                                            <option :value="null">{{ $t('ceremony.given_date') }}</option>
                                            <option :value="'today'">{{ $t('ceremony.today') }}</option>
                                            <option :value="'tomorrow'">{{ $t('ceremony.tomorrow') }}</option>
                                            <option :value="'all'">{{ $t('ceremony.everything') }}</option>
                                        </select>
                                    </td>
                                    <td><input :placeholder="$t('ceremony.min_duration')" @change="loadCeremonies(1)" class="form-control form-control-filtering" type="text" v-model="filtering.duration" /></td>
                                    <td>
                                        <select class="form-control form-control-filtering" v-model="filtering.user_id" @change="loadCeremonies(1)">
                                            <option :value="null">{{ $t('general.select_option') }}</option>
                                            <option :key="user.id" :value="user.id" v-for="user of users">{{ user.name }}</option>
                                        </select>
                                    </td>
                                    <td />
                                </tr>

                                <tr v-for="ceremony of ceremonies.data">
                                    <td>
                                        <div>
                                            {{ ceremony.name + (ceremony.attendee_name ? ` - ${ceremony.attendee_name}` : '') }}
                                        </div>

                                        <div v-if="ceremony.cleaned_up_at">
                                            <span
                                                class="badge badge-warning float-left mr-1"
                                                v-tooltip="$t('ceremony.cleaned_up_message', {type: ceremony.translated_type})"
                                            >
                                            {{ $t('ceremony.cleaned_up') }}
                                        </span>
                                        </div>
                                        <div v-else>
                                            <div class="d-flex align-items-center">
                                                <span
                                                    v-if="isOfflineAvailableForPlayout(ceremony)"
                                                    class="badge badge-success float-left mr-1"
                                                    v-tooltip="$t('ceremony.available_for_playback_tooltip', {date:$root.formatDateTime(ceremony.last_fetched_at)})"
                                                >
                                                    {{ $t('ceremony.available_for_playback') }}
                                                </span>
                                                <span
                                                    v-else-if="needsOfflineUpdateForPlayout(ceremony)"
                                                    class="badge badge-warning float-left mr-1"
                                                    v-tooltip="$t('ceremony.new_version_available_tooltip', {type: ceremony.translated_type, date: $root.formatDateTime(ceremony.updated_at)})"
                                                >
                                                    {{ $t('ceremony.new_version_available') }}
                                                </span>
                                                <span
                                                    v-else
                                                    class="badge badge-secondary float-left mr-1"
                                                    v-tooltip="$t('ceremony.not_available_to_play_tooltip', {type: ceremony.translated_type})"
                                                >
                                                    {{ $t('ceremony.not_available_to_play') }}
                                                </span>

                                                <span
                                                    v-if="!ceremony.cleaned_up_at"
                                                    class="badge badge-secondary pointer-cursor"
                                                    v-tooltip="$t('ceremony.update_for_offline_usage', {type: ceremony.translated_type})"
                                                    @click="downloadCeremony(ceremony)"
                                                >
                                                    <icon name="cloud-download-alt" class="m-0" />
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        {{ ceremony.code }}
                                        <small style="font-size: 10px; display: block; margin-top: -5px;">{{ ceremony.translated_type }}</small>
                                    </td>

                                    <td>
                                        <span class="badge badge-info" v-if="ceremony.date_unknown">{{ $t('ceremony.type_unknown') }}</span>
                                        <span v-else>{{ $root.formatDateTime(ceremony.date) }}</span>
                                    </td>
                                    <td>{{ ceremony && ceremony.type !== 'condolence' ? $root.minutesToTimeInput(ceremony.total_duration) : '-' }}</td>
                                    <td>
                                        {{ ceremony?.user?.name ?? '-' }}
                                    </td>
                                    <td class="text-right align-middle">
                                        <button
                                            v-if="!ceremony.cleaned_up_at && !ceremony.is_processing"
                                            v-tooltip="$t('ceremony.save_pdf_tooltip', {type: ceremony.translated_type})"
                                            class="btn btn-xs btn-outline-primary"
                                            @click="saveCeremonyPdf(ceremony)"
                                        >
                                            <icon name="book" />
                                        </button>

                                        <router-link
                                            v-if="!ceremony.cleaned_up_at"
                                            v-tooltip="$t('ceremony.design_type', {type: ceremony.translated_type})"
                                            tag="button"
                                            class="btn btn-xs btn-primary"
                                            :to="{ name: 'ceremony-design', params: { ceremonyId: ceremony.id }}"
                                        >
                                            {{ $t('ceremony.design') }}
                                        </router-link>

                                        <router-link
                                            v-if="!ceremony.cleaned_up_at"
                                            tag="button"
                                            class="btn btn-xs btn-dark"
                                            :to="{ name: 'ceremony-control', params: { ceremonyId: ceremony.id }}"
                                            v-tooltip="$t('ceremony.control_type', {type: ceremony.translated_type})"
                                        >
                                            {{ $t('ceremony.control') }}
                                        </router-link>

                                        <div class="ml-3 mr-3 dropdown dropdown-animate d-inline">
                                            <a v-tooltip="$t('ceremony.click_for_more_options')" href="#" @click.prevent.stop="openCeremonyPopover = ceremony">
                                                <icon name="ellipsis-h" />
                                            </a>

                                            <div class="dropdown-menu dropdown-menu-sm dropdown-menu-right dropdown-menu-arrow p-3 show" v-if="openCeremonyPopover === ceremony">
                                                <h6 class="dropdown-header px-0 mb-2 text-primary">{{ ceremony.name }}</h6>
                                                <a
                                                    v-if="canSendInvite(ceremony)"
                                                    class="pl-0 dropdown-item"
                                                    href="#"
                                                    @click.prevent.stop="currentAction = { ceremony, action: Action.SendInvite }; openCeremonyPopover = null"
                                                >
                                                    {{ $t('ceremony.send_invite') }}
                                                </a>
                                                <a
                                                    v-if="!ceremony.cleaned_up_at"
                                                    class="pl-0 dropdown-item"
                                                    href="#"
                                                    @click.prevent.stop="currentAction = { ceremony, action: Action.Edit }; openCeremonyPopover = null"
                                                >
                                                    {{ $t('ceremony.edit_data') }}
                                                </a>
                                                <a
                                                    class="pl-0 dropdown-item"
                                                    href="#"
                                                    v-if="(store.getters.hasPermission('deleteOwnCeremonies') && ceremony.user_id === store.state.user.id) || store.getters.hasPermission('deleteCeremonies')"
                                                    @click.prevent.stop="currentAction = { ceremony, action: Action.Delete }; openCeremonyPopover = null"
                                                >
                                                    {{ $t('general.delete') }}
                                                </a>
                                            </div>
                                        </div>
                                    </td>
                                </tr>

                                <tr v-if="ceremonies.data.length === 0 && !isLoading">
                                    <td colspan="100%" class="no-results">
                                        {{ $t('ceremony.no_ceremonies_found') }}

                                        <div class="text-center">
                                            <button @click="currentAction = { action: Action.Create }" class="btn btn-primary btn-xs">
                                                {{ $t('ceremony.new_ceremony_or_condoleance') }}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <DeleteCeremonyModal
                            v-if="currentAction?.action === Action.Delete && currentAction.ceremony"
                            :ceremony="currentAction.ceremony"
                            @deleted="loadCeremonies(ceremonies.current_page, true); currentAction = null"
                            @close="currentAction = null"
                        />

                        <InviteCeremonyModal
                            v-if="currentAction?.action === Action.SendInvite && currentAction.ceremony"
                            :ceremony="currentAction.ceremony"
                            @sent="currentAction = null"
                            @close="currentAction = null"
                        />

                        <CreateCeremonyModal
                            v-if="currentAction?.action === Action.Create"
                            @created="loadCeremonies(ceremonies.current_page, true); currentAction = null"
                            @close="currentAction = null"
                        />

                        <EditCeremonyModal
                            v-if="currentAction?.action === Action.Edit && currentAction.ceremony"
                            :ceremony="currentAction.ceremony"
                            @updated="loadCeremonies(ceremonies.current_page, true); currentAction = null"
                            @close="currentAction = null"
                        />
                    </div>

                    <div class="card-footer border-0">
                        <Pagination :pagination="ceremonies" @change="loadCeremonies" />
                    </div>
                </div>
            </div>
        </div>
    </AuthLayout>
</template>
<script setup>

    import { inject, onMounted, onUnmounted, ref } from 'vue';
    import { IpcCall, ipcCall } from '../utils/ipcCall';
    import Pagination from '../../components/Pagination.vue';
    import DatetimePicker from '../../components/DatetimePicker.vue';
    import Ordering from '../../components/Ordering.vue';
    import moment from 'moment/moment';
    import AuthLayout from './AuthLayout.vue';
    import FullscreenLoading from '../../components/FullscreenLoading.vue';
    import DeleteCeremonyModal from './DeleteCeremonyModal.vue';
    import InviteCeremonyModal from './InviteCeremonyModal.vue';
    import CreateCeremonyModal from './CreateCeremonyModal.vue';
    import EditCeremonyModal from './EditCeremonyModal.vue';
    import { STATE } from '../../enums/state';
    import CeremonyDownloadLoading from './CeremonyDownloadLoading.vue';
    import { useRoute } from 'vue-router/composables';
    import i18n from '../../utils/locale';
    import { notifyError, notifySuccess } from '../../utils/notify';

    const route = useRoute();
    const store = inject('store');

    const Action = {
        Create: 'create',
        Delete: 'delete',
        Edit: 'edit',
        SendInvite: 'invite',
    };

    const ceremonies = ref({
        data: [],
        current_page: 1,
    });

    const users = ref([]);

    const isLoading = ref(false);
    const errorMessage = ref(null);
    const order = ref('date,desc');
    const filtering = ref({
        name: null,
        code: null,
        date: null,
        duration: null,
        user_id: null,
    });
    const datePreset = ref('all');
    const openCeremonyPopover = ref(null);
    const currentAction = ref(null);
    const downloadCeremonyId = ref(null);
    const isDownloadingCeremony = ref(null);
    const isLoadingPdfExport = ref(false);

    onMounted(async () => {
        await loadCeremonies(1, true);
        await loadCompanyUsers();

        if (route.query.sync) {
            await downloadCeremony({
                id: Number(route.query.sync),
            });
        }

        document.addEventListener('click', documentClick);
    });

    onUnmounted(() => {
        document.removeEventListener('click', documentClick);
    });

    function documentClick() {
        openCeremonyPopover.value = null;
    }

    function dateChanged() {
        if (moment(filtering.value.date).isSame(moment(), 'day')) {
            datePreset.value = 'today';
        } else if (moment(filtering.value.date).isSame(moment().add(1, 'day'), 'day')) {
            datePreset.value = 'tomorrow';
        } else if (!filtering.value.date) {
            datePreset.value = 'all';
        } else {
            datePreset.value = null;
        }

        loadCeremonies(1);
    }

    function datePresetChanged() {
        if (datePreset.value === 'today') {
            filtering.value.date = moment().toISOString();
        } else if (datePreset.value === 'tomorrow') {
            filtering.value.date = moment().add(1, 'day').toISOString();
        } else if (datePreset.value === 'all') {
            filtering.value.date = null;
        }

        loadCeremonies(1);
    }

    function canSendInvite(ceremony) {
        return !ceremony.cleaned_up_at && ceremony.state_id === STATE.OPEN && moment().isSameOrBefore(moment(ceremony.date)) && ceremony.attendee;
    }

    async function loadCeremonies(page = 1, loadFromExternal = false) {
        errorMessage.value = null;
        isLoading.value = true;

        try {
            ceremonies.value = await ipcCall(IpcCall.PaginateCeremonies, {
                loadFromExternal,
                page,
                filtering: filtering.value,
                order: order.value,
            });
        } catch (exception) {
            errorMessage.value = exception?.message || i18n.t('general.something_went_wrong');
            console.error(exception);
        } finally {
            isLoading.value = false;
        }
    }

    async function loadCompanyUsers() {
        errorMessage.value = null;
        isLoading.value = true;

        try {
            users.value = await ipcCall(IpcCall.GetAllCeremonyUsers);
        } catch (exception) {
            console.error(exception);
        } finally {
            isLoading.value = false;
        }
    }

    async function downloadCeremony(ceremony) {
        downloadCeremonyId.value = ceremony.id;
        isDownloadingCeremony.value = true;

        try {
            await ipcCall(IpcCall.DownloadCeremony, {
                ceremonyId: ceremony.id,
            });

            await loadCeremonies(ceremonies.value.current_page, true);

            notifySuccess(i18n.t('ceremony.downloaded_ceremony', { type: ceremony.translated_type }));
        } catch (exception) {
            notifyError(exception, i18n.t('ceremony.cant_update_ceremony', { type: ceremony.translated_type }));
        } finally {
            isDownloadingCeremony.value = false;
            downloadCeremonyId.value = null;
        }
    }

    async function saveCeremonyPdf(ceremony) {
        isLoadingPdfExport.value = true;

        try {
            await ipcCall(IpcCall.SaveCeremonyPdfExport, {
                ceremonyId: ceremony.id,
                name: ceremony.name,
                date: ceremony.date
            });

            notifySuccess(i18n.t('ceremony.saved_ceremony_pdf', { type: ceremony.translated_type }));
        } catch (exception) {
            notifyError(exception, i18n.t('general.something_went_wrong'));
        } finally {
            isLoadingPdfExport.value = false;
        }
    }

    function isOfflineAvailableForPlayout(ceremony) {
        return ceremony.last_fetched_at && (ceremony.last_fetched_at >= ceremony.updated_at);
    }

    function needsOfflineUpdateForPlayout(ceremony) {
        return ceremony.last_fetched_at && (ceremony.last_fetched_at < ceremony.updated_at);
    }

    function isOfflineNotAvailableForPlayout(ceremony) {
        return !ceremony.last_fetched_at;
    }
</script>
